/** @jsxImportSource @emotion/react */
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import { useMemo, useState } from 'react';
import { HashLink } from 'react-router-hash-link';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import {
  BLACK,
  WHITE,
  GRAY_MID,
  GRAY_COLD,
  GRAY_DARK,
  PURPLE_FEEK,
  GRAY_LIGHTER,
  WHITE_ALMOST,
  PURPLE_MEDIUM,
  RED_NOTIFICATIONS,
} from 'styles/colors';
import {
  ROUTES_FEEKAPP,
  ROUTES_MANAGER,
  ROUTES_AGNOSTIC,
} from 'constants/routes';
import Text from 'components/Text';
import Icon from 'components/Icon';
import Avatar from 'components/Avatar';
import { logout } from 'redux/app/feekApp';
import Button from 'components/buttons/Button';
import getFormattedUrl from 'utils/getFormattedUrl';
import { getNumberAbbreviation } from 'utils/format';
import { landingIds } from 'screens/manager/Landing';
import { MEDIA_QUERIES, Z_INDEX } from 'styles/constants';
import ButtonPrimary from 'components/buttons/ButtonPrimary';
import { ReactComponent as Info } from 'assets/icons/Info.svg';
import { ReactComponent as Bell } from 'assets/icons/Bell.svg';
import getClassNameFromArray from 'utils/getClassNameFromArray';
import { ReactComponent as LogOut } from 'assets/icons/LogOut.svg';
import DropDownLabelAndIcon from 'components/DropDownLabelAndIcon';
import { ReactComponent as ZapMini } from 'assets/icons/ZapMini.svg';
import { ReactComponent as Chevron } from 'assets/icons/Chevron.svg';
import { ReactComponent as Calendar } from 'assets/icons/Calendar.svg';
import { ReactComponent as Question } from 'assets/icons/Question.svg';
import { ReactComponent as FeekLogo } from 'assets/icons/FeekLogo.svg';
import { ReactComponent as AvatarIcon } from 'assets/icons/Avatar.svg';
import { ReactComponent as Location } from 'assets/icons/Location.svg';
import { ReactComponent as LocationMini } from 'assets/icons/LocationMini.svg';
import CampaignsSearchInput from 'components/inputs/InputSearchCampaigns/Index';
import { ReactComponent as HamburguerButton } from 'assets/icons/HamburguerButton.svg';

export const headerHeight = { normal: '88px', mobile: '60px' };

const styles = css`
  display: flex;
  width: 100vw;
  height: ${headerHeight.normal};
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 20px 70px;
  border-bottom: 1px solid ${GRAY_LIGHTER};
  background-color: ${WHITE};
  position: fixed;
  top: 0;
  z-index: ${Z_INDEX.header};

  .justify-content-start {
    display: flex;
    justify-content: start;
    gap: 20px;
  }
  .showOnMobileOnly {
    display: none;
  }

  .feek-logo {
    margin-inline-end: 20px;
  }

  .location-container {
    display: flex;
    align-items: center;
    padding: 13px 20px 13px 20px;
    border-radius: 14px;
    border: 1px solid ${PURPLE_MEDIUM};

    .location-text {
      margin-inline: 10px 5px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 320px;
    }
  }

  .searchInputContainer {
    width: 300px;
  }
  .search-wrapper {
    width: clamp(250px, 35vw, 600px);
    justify-content: end;

    .search-input {
      margin-left: auto;
      align-self: center;
      background-color: ${GRAY_COLD};
      border-radius: 10px;
      border: none;
    }
  }

  .menu-wrapper {
    display: flex;
    gap: 20px;

    .user-menu {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 20px;
      border-radius: 30px;
      height: 50px;
      padding: 4px 4px 4px 25px;
    }
  }

  ${MEDIA_QUERIES.tablet} {
    .hideOnTablet {
      display: none !important;
    }
    .search-wrapper {
      width: clamp(180px, 28vw, 300px);
    }
    .menu-wrapper {
      gap: 10px;

      .feek-points-button > div {
        gap: 2.5px;
        padding-inline: 14px;
      }

      .notifications-button {
        position: relative;

        > div {
          display: flex;
          align-items: center;
          height: 32px;
        }
      }
    }

    .showOnMobileOnly {
      display: revert;
    }
  }

  ${MEDIA_QUERIES.mobile} {
    height: ${headerHeight.mobile};
    padding: 10px 12px;
    justify-content: space-between;
    gap: 8px;

    .hideOnMobile {
      display: none;
    }

    .justify-content-start {
      gap: 8px;
      flex: 1;
      min-width: 0;
    }

    .button-back-responsive {
      background-color: ${WHITE_ALMOST};
      border-radius: 50%;
      padding: 9px;
      aspect-ratio: 1;
      height: 32px;
      width: 32px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
    }

    .feek-logo {
      margin: 0;
      flex-shrink: 0;
    }

    .location-container {
      flex-shrink: 0;
      padding: 6px 10px;

      .location-text {
        display: none;
      }
    }

    .search-wrapper {
      flex: 1;
      min-width: 0;
      max-width: 130px;

      > div {
        height: 34px;
        font-size: 13px;

        .search-input {
          font-size: 13px;
          padding-block: 0;
        }
      }
    }

    .menu-wrapper {
      flex-shrink: 0;
      gap: 8px;
    }

    .user-menu {
      border: none;
      padding: 0;
      height: auto;
      min-width: unset;
      background: transparent;
      margin-inline-end: -4px;

      .iconLabelWrapper {
        display: none;
      }

      .dropDownBtn {
        width: auto;
        height: auto;
      }
    }
  }
`;

const menuStyles = css`
  .content {
    right: calc(-100vw) !important;
    left: unset !important;
    width: 350px;
    display: flex;
    position: relative;
    flex-direction: column;
    margin-block-start: 15px;
    padding: 30px 0;
    background: ${WHITE};
    border-radius: 16px;
    border: 1px solid ${GRAY_MID};
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
    overflow-y: scroll;
    max-height: 100vh;

    .menu-item {
      display: flex;
      padding: 18px 20px;
      margin-left: 8px;
      margin-right: 8px;
      align-items: center;
      border-bottom: 1px solid ${GRAY_LIGHTER};

      :hover {
        color: ${PURPLE_FEEK};
      }
    }

    .border-bottom-none {
      border-bottom: none;
    }
    > * {
      padding-inline: 30px;
      font-size: 16;
      font-weight: 600;
      color: ${BLACK};
      white-space: nowrap;

      &.border-bottom {
        padding-block-end: 15px;
        border-bottom: 1px solid ${GRAY_MID};

        & ~ * {
          font-weight: normal;
        }
      }

      &.notifications-link {
        display: flex;

        .notifications-count {
          display: inline-flex;
          justify-content: center;
          align-items: center;
          margin-inline-start: 10px;
          height: 20px;
          min-width: 20px;
          padding-inline: 6px;
          border-radius: 100vmax;
          background-color: ${RED_NOTIFICATIONS};
        }
      }
    }
  }

  .leftIcon {
    margin-right: 10px;
  }
`;

const Header = ({ setShowSelectLocationModal, responsiveAction }) => {
  const { pathname } = useLocation();

  const dispatch = useDispatch();
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const {
    avatar,
    fullName,
    username,
    instagramUserName,
    notificationsCount,
    totalFeekPoints,
  } = useSelector((state) => state.feekApp);
  const notificationsCountFormatted = useMemo(
    () => getNumberAbbreviation(notificationsCount),
    [notificationsCount],
  );

  const currentFeekToken = localStorage.getItem('currentFeekToken');
  const handleLogout = () => {
    setMenuIsOpen(false);
    dispatch(logout());
  };

  const dataDependentStyles = css`
    ${MEDIA_QUERIES.tablet} {
      ${!!notificationsCount &&
      `.menu-wrapper > .notifications-button:after {
        content: '${notificationsCountFormatted}';
        color: ${WHITE};
        position: absolute;
        bottom: 18px;
        aspect-ratio: 1;
        height: 14px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 8px;
        background: ${RED_NOTIFICATIONS};
        border-radius: 50%;
      }`}

      ${pathname !== ROUTES_FEEKAPP.EXPLORE &&
      pathname !== ROUTES_FEEKAPP.EXPLORE_REWARDS &&
      `.location-container {
          display: none !important;
        }
      `}
    }
  `;

  return (
    <header css={[styles, dataDependentStyles]}>
      <div className="justify-content-start">
        {responsiveAction && (
          <Button
            onClick={responsiveAction}
            className="button-back-responsive showOnMobileOnly"
          >
            <Icon
              iconMobile={Chevron}
              icon={Chevron}
              color={BLACK}
              widthMobile={12}
              rotate={90}
            />
          </Button>
        )}
        <Link
          className={getClassNameFromArray([
            'feek-logo',
            responsiveAction ? 'hideOnMobile' : null,
          ])}
          to={ROUTES_FEEKAPP.EXPLORE}
        >
          <Icon
            height={44}
            icon={FeekLogo}
            heightMobile={28}
            color={PURPLE_FEEK}
          />
        </Link>

        <Button
          className={getClassNameFromArray(['location-container'])}
          onClick={() => setShowSelectLocationModal(true)}
        >
          <Icon
            height={22}
            color={BLACK}
            icon={Location}
            heightMobile={14}
            iconMobile={LocationMini}
          />
        </Button>

        <CampaignsSearchInput
          className="search-wrapper"
          placeholder="Buscar lugares, comida, promociones, categorías..."
        />
      </div>

      <div className="menu-wrapper">
        {currentFeekToken ? (
          <>
            <Link
              className="feek-points-button showOnMobileOnly"
              to={ROUTES_FEEKAPP.PROFILE_FEEKER_EDIT}
            >
              <ButtonPrimary
                textColor={BLACK}
                label={getNumberAbbreviation(totalFeekPoints)}
                height={32}
                fontSizeMobile={14}
                heightMobile={28}
                textProps={{ fontSize: 16, fontWeight: 600 }}
                strokeVariant
                strokeColor={GRAY_MID}
                pillShape
                layoutOnly
                distribution="center"
                icon={
                  <Icon
                    height={16}
                    color={BLACK}
                    icon={ZapMini}
                    heightMobile={14}
                  />
                }
              />
            </Link>

            <Link
              className="notifications-button showOnMobileOnly"
              to={ROUTES_FEEKAPP.NOTIFICATIONS}
            >
              <Button layoutOnly>
                <Icon
                  size={22}
                  color={BLACK}
                  icon={Bell}
                  sizeMobile={18}
                />
              </Button>
            </Link>
          </>
        ) : (
          <>
            <Link
              className="hideOnTablet hideOnMobile menu-item"
              to={ROUTES_FEEKAPP.REGISTRATION}
            >
              <ButtonPrimary
                textColor={WHITE}
                label="Regístrate"
                color={GRAY_DARK}
                fontSize={16}
                width={125}
                pillShape
                layoutOnly
              />
            </Link>

            <Link
              className="showOnMobileOnly"
              to={ROUTES_FEEKAPP.LOGIN}
            >
              <ButtonPrimary
                textColor={BLACK}
                label="Ingresar"
                height={32}
                fontSize={16}
                strokeVariant
                strokeColor={GRAY_MID}
                pillShape
                layoutOnly
              />
            </Link>
          </>
        )}

        <DropDownLabelAndIcon
          childrenWrapperCSS={menuStyles}
          className="user-menu hideOnTablet"
          isOpen={menuIsOpen}
          onOpen={() => setMenuIsOpen(true)}
          onClose={() => setMenuIsOpen(false)}
          leftIcon={
            <Icon icon={HamburguerButton} color={BLACK} width={24} />
          }
          rightIcon={<Avatar image={avatar} size={40} />}
        >
          {currentFeekToken ? (
            <>
              <div className="d-flex justify-content-start align-items-center mb-3">
                <Avatar image={avatar} size={60} />
                <div className="ms-2">
                  <Text fontWeight={600} fontSize={16}>
                    {fullName || username}
                  </Text>
                  <Text>@{instagramUserName}</Text>
                </div>
              </div>
              <Link
                to={ROUTES_FEEKAPP.PROFILE_FEEKER_EDIT}
                className="menu-item"
              >
                <Icon
                  size={22}
                  color={BLACK}
                  icon={AvatarIcon}
                  className="leftIcon"
                  sizeMobile={18}
                />
                Perfil
              </Link>
              <Link
                className="notifications-link menu-item"
                to={ROUTES_FEEKAPP.NOTIFICATIONS}
              >
                <Icon
                  size={22}
                  color={BLACK}
                  icon={Bell}
                  className="leftIcon"
                  sizeMobile={18}
                />
                Notificaciones
                {!!notificationsCount && (
                  <Text
                    fontWeight={600}
                    color={WHITE}
                    type="inline"
                    align="center"
                    className="notifications-count"
                  >
                    {notificationsCountFormatted}
                  </Text>
                )}
              </Link>

              <Link to={ROUTES_FEEKAPP.AGENDA} className="menu-item">
                <Icon
                  size={22}
                  color={BLACK}
                  icon={Calendar}
                  className="leftIcon"
                  sizeMobile={18}
                />
                Agenda
              </Link>

              <Link to={ROUTES_FEEKAPP.INFO} className="menu-item">
                <Icon
                  size={22}
                  color={BLACK}
                  icon={Question}
                  className="leftIcon"
                  sizeMobile={18}
                />
                Ayuda / Soporte
              </Link>
              <Link to={ROUTES_AGNOSTIC.POLICY} className="menu-item">
                <Icon
                  size={22}
                  color={BLACK}
                  icon={Info}
                  className="leftIcon"
                  sizeMobile={18}
                />
                Aviso de privacidad
              </Link>
              <Link
                to={ROUTES_AGNOSTIC.TERMS_AND_CONDITIONS}
                className="menu-item"
              >
                <Icon
                  size={22}
                  color={BLACK}
                  icon={Info}
                  className="leftIcon"
                  sizeMobile={18}
                />
                Términos y condiciones
              </Link>

              <Button
                className="logout-button menu-item mb-2"
                onClick={handleLogout}
              >
                <Icon
                  size={22}
                  color={BLACK}
                  icon={LogOut}
                  className="leftIcon"
                  sizeMobile={18}
                />
                Cerrar sesión
              </Button>
              <ButtonPrimary
                pillShape
                width={200}
                label="Cerrar"
                onClick={() => setMenuIsOpen(false)}
                className="mt-3 mx-auto"
                color={PURPLE_FEEK}
              />
            </>
          ) : (
            <>
              <Link
                to={ROUTES_FEEKAPP.REGISTRATION}
                className="menu-item pt-0"
              >
                Regístrate
              </Link>
              <Link className=" menu-item" to={ROUTES_FEEKAPP.LOGIN}>
                Iniciar Sesión
              </Link>

              <HashLink
                to={getFormattedUrl({
                  route: ROUTES_MANAGER.LANDING,
                  hash: landingIds.signInForm,
                  isManagerRoute: true,
                })}
                className="menu-item border-bottom-none pb-1"
              >
                Pon tu negocio en Feek
              </HashLink>
            </>
          )}
        </DropDownLabelAndIcon>
      </div>
    </header>
  );
};

Header.propTypes = {
  setShowSelectLocationModal: PropTypes.func,
  responsiveAction: PropTypes.func,
};

export default Header;
